/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Stream, StreamPlayerApi } from '@cloudflare/stream-react';
import { Button } from '@myinterview/component-library';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PlayIcon } from './icons/PlayIcon';
import { VideoTimeBar } from './VideoTimeBar';
import { formattedDuration } from '../utils/formatters.utils';
import { VIDEO_INACTIVE_TIMEOUT } from '../utils/constants.utils';
import { EVENT_TYPES, ITrackEvent } from '../services/event.service';

interface IProps {
  src: string;
  isThinkingTime: boolean;
  onVideoEnds: (count: number) => void;
  hidden?: boolean;
  isWidgetMinimized: boolean;
  onVideoStateChanged?: (eventData: ITrackEvent) => void;
}

const CLOULDFLARE_MARGIN_TIME = 1; // 1 second

export const VideoQuestion:React.FC<IProps> = ({
  src, isThinkingTime, onVideoEnds, hidden = false, isWidgetMinimized, onVideoStateChanged,
}) => {
  const { t } = useTranslation();
  const isCloudflare = ['watch.videodelivery.net', 'cloudflarestream'].some((s) => src.includes(s));
  const cloudflareSrc = isCloudflare
    ? src.includes('watch.videodelivery.net')
      ? src.split('net/')[1]
      : src.split('cloudflarestream.com/')[1]?.split('/')[0]
    : '';

  const reactPlayerSrc = src.replace('video-recording-client.s3.ap-southeast-2.amazonaws.com/videos/', 'video.myinterview.com/mp4/720p/');

  const cloudFlarePlayerRef = useRef<StreamPlayerApi>();
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isControlVisible, setIsControlVisible] = useState(false);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [error, setError] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const activeTimeout = useRef<NodeJS.Timeout>();
  const videoEndedCount = useRef(0);

  const onPlayVideo = () => {
    if (!cloudFlarePlayerRef.current && !reactPlayerRef.current) return;
    if (cloudFlarePlayerRef.current) {
      setDuration(cloudFlarePlayerRef.current.duration);
      cloudFlarePlayerRef.current.play();
    } else if (reactPlayerRef.current) {
      setDuration(reactPlayerRef.current.getDuration());
    }
    setIsControlVisible(true);
    setIsPlaying(true);
    onVideoStateChanged?.({ eventType: EVENT_TYPES.VIDEO_QUESTION_PLAY, extraData: { currentTime, duration, isVideoEnded, videoEndedCount: videoEndedCount.current } });
  };

  const onPauseVideo = () => {
    if ((!cloudFlarePlayerRef.current && !reactPlayerRef.current) || isThinkingTime) return;
    if (cloudFlarePlayerRef.current) {
      cloudFlarePlayerRef.current.pause();
    }
    setIsPlaying(false);
    onVideoStateChanged?.({ eventType: EVENT_TYPES.VIDEO_QUESTION_PAUSE, extraData: { currentTime, duration, isVideoEnded, videoEndedCount: videoEndedCount.current } });
  };

  const onCloudFlareTimeUpdate = (time?: number) => {
    setCurrentTime(time ?? cloudFlarePlayerRef.current?.currentTime ?? 0);
  };

  const onReactPlayerProgress = (time?: number) => {
    setCurrentTime(time ?? reactPlayerRef.current?.getCurrentTime() ?? 0);
  };

  const handleError = (err: any, data?: any, hlsInstance?: any, hlsGlobal?: any) => {
    console.log('onError');
    if (reactPlayerRef.current) {
      console.log('error', err);
      console.log('data', data);
      console.log('hlsInstance', hlsInstance);
      console.log('hlsGlobal', hlsGlobal);
    }
    setError(true);
  };

  const onChangeTime = (time: number, playing: boolean) => {
    if (isThinkingTime) return;
    if (cloudFlarePlayerRef.current) {
      cloudFlarePlayerRef.current.pause();
      if (playing) cloudFlarePlayerRef.current.play();
      cloudFlarePlayerRef.current.currentTime = time;
      onCloudFlareTimeUpdate(time);
      setIsPlaying(true);
    } else if (reactPlayerRef.current) {
      setIsPlaying(false);
      const acceptedTime = Math.max(time, 1);
      reactPlayerRef.current.seekTo(acceptedTime);
      onReactPlayerProgress(acceptedTime);
      if (playing) setIsPlaying(true);
    }
  };

  const handleVideoEnds = () => {
    videoEndedCount.current += 1;
    onVideoEnds(videoEndedCount.current);
    setIsPlaying(false);
    setIsControlVisible(false);
    setIsVideoEnded(true);
  };

  const onUserActive = () => {
    clearTimeout(activeTimeout.current);
    setIsActive(true);
    activeTimeout.current = setTimeout(() => {
      setIsActive(false);
    }, VIDEO_INACTIVE_TIMEOUT);
  };

  useEffect(() => {
    if (isWidgetMinimized && !isThinkingTime) {
      if (isCloudflare) cloudFlarePlayerRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isWidgetMinimized]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isCloudflare && duration && (duration - currentTime < CLOULDFLARE_MARGIN_TIME)) {
      timeout = setTimeout(handleVideoEnds, CLOULDFLARE_MARGIN_TIME * 1000);
    }

    return () => clearTimeout(timeout);
  }, [currentTime]);

  const videoQuestionClassNames = classNames({
    'myinterview-widget-video-question': true,
    'myinterview-widget-video-question--hidden': hidden,
    'myinterview-widget-video-question--loading': !isReady,
    'myinterview-widget-video-question--done': !isControlVisible && isVideoEnded,
  });

  const videoControlsClassNames = classNames({
    'myinterview-widget-video-question__controls': true,
    'myinterview-widget-video-question__controls--inactive': !isActive && isPlaying,
  });

  const retryButtonClassNames = classNames({
    'myinterview-widget-video-question__retry-button': true,
    'myinterview-widget-video-question__retry-button--hidden': hidden,
  });

  return (
    <>
      <div
        className={videoQuestionClassNames}
        onMouseMove={onUserActive}
        onTouchMove={onUserActive}
      >
        {error ? <></> : (
          isCloudflare
            ? (
              <Stream
                streamRef={cloudFlarePlayerRef}
                className="myinterview-widget-video-question__video"
                src={cloudflareSrc}
                onEnded={handleVideoEnds}
                onTimeUpdate={() => onCloudFlareTimeUpdate()}
                onCanPlay={() => setIsReady(true)}
              />
            )
            : (
              <ReactPlayer
                ref={reactPlayerRef}
                url={reactPlayerSrc}
                playing={isPlaying}
                onPause={() => setIsPlaying(false)}
                playsinline
                onEnded={handleVideoEnds}
                style={{ pointerEvents: 'none' }}
                controls={false}
                width="100%"
                height="100%"
                onProgress={() => onReactPlayerProgress()}
                progressInterval={250}
                onReady={() => setIsReady(true)}
                onError={handleError}
              />
            )
        )}
        <div
          className={`
        myinterview-widget-video-question__play-button-wrapper
        ${!isReady || isPlaying || isControlVisible ? 'myinterview-widget-video-question__play-button-wrapper--hidden' : ''}
        `}
          role="presentation"
          onClick={isPlaying ? onPauseVideo : onPlayVideo}
        >
          <PlayIcon playing={false} />
        </div>

        {isReady && isControlVisible && (
        <div className={videoControlsClassNames}>
          {!isThinkingTime && (
          <div
            className="myinterview-widget-video-question__control-play-button"
            role="presentation"
            onClick={isPlaying ? onPauseVideo : onPlayVideo}
          >
            <PlayIcon playing={isPlaying} />
          </div>
          )}
          <div className="myinterview-widget-video-question__timebar-counter-wrapper">
            <VideoTimeBar duration={duration} currentTime={currentTime} onChangeTime={onChangeTime} isThinkingTime={isThinkingTime} />
            {!Number.isNaN(currentTime) && !Number.isNaN(duration) && (
            <div className="myinterview-widget-video-question__counter-wrapper">
              {`${formattedDuration(currentTime)} / ${formattedDuration(duration)}`}
            </div>
            )}
          </div>
        </div>
        )}
      </div>
      {error && <Button className={retryButtonClassNames} onClick={() => setError(false)}>{t('buttons.btn_retry')}</Button>}
    </>
  );
};
